import * as React from "react";
import {
  ActionButton,
  DirectionalHint,
  FontWeights,
  Icon,
  IconButton,
  INavButtonProps,
  INavLink,
  INavLinkGroup,
  INavStyles,
  mergeStyleSets,
  Nav,
  SearchBox,
  Stack,
  TooltipDelay,
  TooltipHost,
} from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import * as Messages from "../../codegen/Messages";

export interface ToolTipOptions {
  /**
   * tooltip text
   */
  toolTipText: string;
  /**
   * label for create option
   */
  createLabel?: string;
  /**
   * label for view option
   */
  viewLabel?: string;
  /**
   * when create label is clicked, onCreateClick will be called
   */
  onCreateClick?: () => void;
  /**
    * when view label is clicked, onViewClick will be called
    */
  onViewClick?: () => void;
}
/**
 * Menu has two types: SIDE_MENU, TOP_MENU
 */
export enum MenuType {
  /**
   * SIDE_MENU: This type should be used when menu needs to be shown in application i.e inside details page
   */
  SIDE_MENU,
  /**
   * TOP_MENU: This type should be used when menu needs to be shown as floating i.e menu on header
   */
  TOP_MENU,
}

/**
 * Each row in menu component is defined by MenuItem
 */
export interface MenuItem {
  /**
   * name of the menu item
   */
  name: string;
  /**
   * when menu item is clicked, onClick will be called
   */
  onClick?: (item: MenuItem, ev?: React.MouseEvent<HTMLElement>) => void;
  /**
   * icon name
   */
  icon?: string;
  /**
   * unique id for menu item. It has to be unique in a collection of menu items.
   */
  id: string;
  /**
   * tooltip options i.e create click, view click.
   */
  toolTipOptions?: ToolTipOptions;
  /**
   * if true, menu item will be disabled
   */
  disabled?: boolean;
}

/**
 * Group Item is defined as combination of heading and list of menu items.
 */
export interface GroupItem {
  /**
   * heading. if passed, it will show the menu items under this name.
   */
  heading?: string;
  /**
   * Array of menu items, each item is individual menu row.
   */
  items: MenuItem[];
}

/**
 * Menu component props
 */
export interface MenuProps {
  /**
   * Menu is collapsible
   */
  isCollapsible?: boolean;
  /**
   * Array of menu items (data)
   */
  groups: GroupItem[];
  /**
   * Returns width of menu on load and collapsed/uncollapsed event
   */
  onCollapse?: (width: number) => void;
  /**
   * Searchbox placeholder
   */
  placeHolder?: string;
  /**
   * Menu type (Docked, Floating, Collapsible)
   */
  type: MenuType;
  /**
   * It is the key of the menu item selected
   */
  selectedKey?: string;
  /**
   * Searchbox search icon display
   */
  showSearchIcon?: boolean;
  /**
   * Show Searchbox
   */
  showSearchBox?: boolean;
  /**
   * menu id for test purposes
   */
  testId?: string;
  /**
   * Width of the menu
   * @default 225px
   */
  width?: string | number;
}

const classNames = mergeStyleSets({
  itemToolTipContainer: {
    width: "325px",
    minHeight: "100px",
    padding: "20px 20px 0 20px",
  },
  itemToolTipHeader: { display: "flex" },
  itemToolTipImage: { paddingRight: "15px" },
  itemToolTipTitle: {
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "20px",
    overflow: "hidden",
  },
  svgIconSizeToolTip: {
    svg: {
      height: 48,
      width: 48,
    },
  },
  svgIconSizeNavMenu: {
    svg: {
      height: 18,
      width: 18,
    },
  },
});

const getToolTip = (props: INavButtonProps, content: JSX.Element): JSX.Element => {
  const toolTipOptions = props?.link?.toolTipOptions;
  const showToolTip = !props?.link?.disabled && (toolTipOptions?.onCreateClick || toolTipOptions?.onViewClick);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const toolTipId = useId("tooltip");

  const toolTip = (
    <TooltipHost
      content={
      (
        <div className={classNames.itemToolTipContainer}>
          <div className={classNames.itemToolTipHeader}>
            <div className={classNames.itemToolTipImage}>
              <Icon {...props?.iconProps} className={classNames.svgIconSizeToolTip} />
            </div>
            <div className={classNames.itemToolTipTitle}>
              {toolTipOptions?.toolTipText}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              columnGap: 30,
              marginTop: 20,
              marginBottom: 10,
            }}
          >
            {toolTipOptions?.onCreateClick && (
              <ActionButton
                href="#"
                iconProps={{ iconName: "Add" }}
                onClick={toolTipOptions?.onCreateClick}
              >
                {toolTipOptions?.createLabel || Messages.actions.create()}
              </ActionButton>
            )}
            {toolTipOptions?.onViewClick && (
              <ActionButton
                href="#"
                iconProps={{ iconName: "View" }}
                onClick={toolTipOptions?.onViewClick}
              >
                {toolTipOptions?.viewLabel || Messages.actions.view()}
              </ActionButton>
            )}
          </div>
        </div>
      )
    }
      calloutProps={{ isBeakVisible: false }}
      delay={TooltipDelay.medium}
      directionalHint={DirectionalHint.rightTopEdge}
      id={toolTipId}
    >
      {content}
    </TooltipHost>
  );
  return showToolTip ? toolTip : content;
};

const getUrlTemplate = (props: INavButtonProps): JSX.Element => {
  const { className, onClick, children, disabled } = props;
  const { props: cProps } = children as JSX.Element;
  const inlineStyles = disabled ? { color: "gray", cursor: "default" } : undefined;
  const iconInlineStyles = disabled ? { filter: "grayscale(100%)", opacity: 0.4 } : undefined;
  return (
    <ActionButton
      onClick={disabled ? undefined : onClick}
      style={{ ...inlineStyles, color: "#0078d4", cursor: "pointer" }}
      className={className}
    >
      <span style={{ display: "flex" }}>
        <Icon
          style={{ margin: "1px 4px", ...iconInlineStyles }}
          {...props?.iconProps}
          className={classNames.svgIconSizeNavMenu}
        />
        <div
          style={inlineStyles}
          className={cProps?.className}
        >
          {cProps?.children}
        </div>
      </span>
    </ActionButton>
  );
};

const enum MenuColors {
  BLACK = "#000000",
  BLUE = "#E0E9F1",
  DARK_GRAY = "gray",
  GRAY = "#F1F0EF",
  WHITE = "#FFFFFF",
}
const getHoverColor = (type: MenuType):string => (type === MenuType.SIDE_MENU ? MenuColors.GRAY : MenuColors.BLUE);

/**
 * Menu can be used to navigate to other components by using it as floating and itwill pop up on top of
 * any component, and if you want to show it beside your component in application than use it as side menu type.
 */
export const Menu = ({
  isCollapsible,
  groups,
  type,
  placeHolder,
  selectedKey,
  showSearchIcon,
  showSearchBox,
  onCollapse,
  testId,
  width = 225,
}: MenuProps): JSX.Element => {
  const [collapse, setCollapse] = React.useState<string>();
  const [selected, setSelected] = React.useState<string | undefined>(selectedKey);
  const [navLinks, setNavLinks] = React.useState<INavLinkGroup[]>([]);
  const [collapseWdith, setCollapseWidth] = React.useState<number | string>(width);
  const [chevronIconDirection, setChevronIconDirection] = React.useState<string>("DoubleChevronLeft8");

  React.useEffect(() => {
    if (onCollapse) {
      onCollapse(collapseWdith as number);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseWdith]);
  React.useEffect(() => setSelected(selectedKey), [selectedKey]);

  const collapseMenu = (): void => {
    if (collapse === "none") {
      setCollapse("block");
      setCollapseWidth(width);
      setChevronIconDirection("DoubleChevronLeft8");
    } else {
      setCollapse("none");
      setCollapseWidth(type === MenuType.SIDE_MENU ? 30 : 40);
      setChevronIconDirection("DoubleChevronRight8");
    }
  };

  const onLinkClick = (ev?: React.MouseEvent<HTMLElement>, item?: INavLink): void => {
    ev?.preventDefault?.();
    if (item && item.onItemClick) item?.onItemClick(item, ev);
    if (type === MenuType.SIDE_MENU) setSelected(item?.key);
  };

  const filterOptions = (searchValue?: string): void => {
    const lowerCaseVal = searchValue?.toLowerCase() || "";
    const filteredArray = groups
      .filter(ele => ele.items.some(subEle => subEle.name.toLowerCase().indexOf(lowerCaseVal) > -1))
      .map(ele => Object.assign(
        {},
        ele,
        { items: ele.items.filter(subEle => subEle.name.toLowerCase().indexOf(lowerCaseVal) > -1) },
      ));
    const mItems = mapMenuItems(filteredArray || groups);
    setNavLinks(mItems);
  };

  const mapMenuItems = (menuItems: GroupItem[]): INavLinkGroup[] => {
    const mItems = menuItems?.map(ele => (
      {
        name: ele?.heading,
        links: ele?.items.map(subItem => {
          const toolTipOptions = subItem?.toolTipOptions;
          const title = toolTipOptions?.toolTipText ? toolTipOptions?.toolTipText : "";
          return {
            title: !(toolTipOptions?.onCreateClick || toolTipOptions?.onViewClick) || subItem.disabled ? title : "",
            name: subItem?.name,
            key: subItem?.id,
            icon: subItem?.icon,
            disabled: subItem.disabled,
            url: "",
            target: "_self",
            onItemClick: subItem?.onClick,
            toolTipOptions: subItem?.toolTipOptions,
          };
        }),
      } as INavLinkGroup
    ));
    return mItems;
  };

  React.useEffect(() => {
    const mItems = mapMenuItems(groups);
    setNavLinks(mItems);
  }, [groups]);

  const navStyles: Partial<INavStyles> = {
    root: { height: "auto", overflowY: "auto", overflowX: "hidden" },
    navItems: { display: collapse },
    groupContent: { marginBottom: 0 },
    group: { display: collapse, fontWeight: FontWeights.semibold },
    linkText: {
      display: collapse,
      color: MenuColors.BLACK,
      fontWeight: 400,
      fontSize: 13,
      fontStyle: "normal",
    },
    link: {
      height: 32,
      lineHeight: 32,
      paddingLeft: 7,
      "&::after": { borderLeft: 0 },
      ".ms-Nav-compositeLink:hover &": { background: getHoverColor(type) },
      ".is-disabled:hover &": { background: type === MenuType.SIDE_MENU ? "#FFFFFF" : MenuColors.GRAY },
    },
  };

  const renderGroupHeader = (group?: INavLinkGroup): JSX.Element => (
    <>
      <p style={{ margin: "2px 0px 2px 14px" }}>{group?.name}</p>
      <hr style={{ width: "200px", border: "none", height: "1px", background: "darkgray" }} />
    </>
  );

  if (type === MenuType.TOP_MENU) {
    delete navStyles.group;
    delete navStyles.navItems;
  }

  return (
    <Stack
      data-test-id={testId}
      styles={{
        root: {
          cursor: collapse === "none" && type === MenuType.SIDE_MENU ? "pointer" : "default",
          width: collapseWdith,
          height: "100%",
          ":hover": { background: collapse === "none" ? getHoverColor(type) : "" },
          backgroundColor: type === MenuType.SIDE_MENU ? MenuColors.WHITE : MenuColors.GRAY,
          boxShadow: type === MenuType.TOP_MENU
            ? "0 6.4px 14.4px 0 rgb(0 0 0 / 13%), 0 1.2px 3.6px 0 rgb(0 0 0 / 11%)" : "",
        },
      }}
      onClick={(type === MenuType.SIDE_MENU && collapse === "none") ? collapseMenu : undefined}
    >
      {isCollapsible && (
        <Stack
          horizontal
          styles={{
            root: {
              cursor: "pointer",
              justifyContent: type === MenuType.SIDE_MENU && showSearchBox ? "center" : "end",
              alignItems: "center",
              "&:hover": { backgroundColor: getHoverColor(type) },
            },
          }}
          onClick={((type === MenuType.TOP_MENU)
            || (type === MenuType.SIDE_MENU && !showSearchBox)) ? collapseMenu : undefined}
        >
          {type === MenuType.SIDE_MENU && showSearchBox && (
            <Stack styles={{ root: { display: collapse } }}>
              <SearchBox
                styles={{
                  root: {
                    width: (parseInt(width.toString(), 10) - 40),
                    height: 25,
                    marginLeft: 5,
                  },
                }}
                placeholder={placeHolder}
                showIcon={showSearchIcon}
                onClear={() => filterOptions("")}
                onChange={(_, searchValue) => filterOptions(searchValue)}
                onSearch={searchValue => filterOptions(searchValue)}
              />
            </Stack>
          )}
          {isCollapsible && (
            <IconButton
              iconProps={{
                iconName: chevronIconDirection,
                styles: {
                  root: {
                    fontSize: 10,
                    "button:hover &": { color: MenuColors.DARK_GRAY },
                    ":hover": { background: getHoverColor(type) },
                  },
                },
              }}
              styles={{
                root: {
                  color: MenuColors.DARK_GRAY,
                  ":hover": {
                    backgroundColor: getHoverColor(type),
                    background: getHoverColor(type),
                  },
                },
              }}
              onClick={type === MenuType.SIDE_MENU ? collapseMenu : undefined}
              title={collapse === "none" ? Messages.ariaLabel.expand() : Messages.ariaLabel.collapse()}
              ariaLabel={collapse === "none" ? Messages.ariaLabel.expand() : Messages.ariaLabel.collapse()}
            />
          )}
        </Stack>
      )}
      <Stack>
        <Nav
          styles={navStyles}
          onRenderGroupHeader={renderGroupHeader}
          groups={navLinks}
          onLinkClick={onLinkClick}
          initialSelectedKey={selected}
          selectedKey={selected}
          linkAs={
            props => (type === MenuType.SIDE_MENU ? getUrlTemplate(props) : getToolTip(props, getUrlTemplate(props)))
          }
        />
      </Stack>
    </Stack>
  );
};
